import { SyntheticEvent, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  usePlacesAutocomplete,
  type PlacePredictionItem
} from '../../hooks/usePlacesAutocomplete'

export type AddressAutocompleteProps = {
  value: { lat: number; lng: number; displayName: string } | null
  onChange: (
    value: { lat: number; lng: number; displayName: string } | null
  ) => void
  error?: boolean
  helperText?: string
  disabled?: boolean
}

const AddressAutocomplete = ({
  value,
  onChange,
  error,
  helperText,
  disabled: externalDisabled
}: AddressAutocompleteProps) => {
  const {
    apiState,
    predictions,
    isLoadingPredictions,
    isResolvingPlace,
    resolutionError,
    setInput,
    selectPrediction,
    clearPredictions,
    reset
  } = usePlacesAutocomplete({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '',
    includedPrimaryTypes: ['geocode']
  })

  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [geocodeError, setGeocodeError] = useState<string | null>(null)

  const shouldShowPopup = open && inputValue.length >= 3

  const handleInputChange = (
    _event: SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'clear' || newValue === '') {
      clearPredictions()
      setInputValue('')
      setGeocodeError(null)
      onChange(null)
      return
    }

    setInputValue(newValue)
    setGeocodeError(null)
    setInput(newValue)
  }

  const handleChange = async (
    _event: SyntheticEvent,
    newValue: string | PlacePredictionItem | null
  ) => {
    if (newValue === null || newValue === '') {
      clearPredictions()
      setInputValue('')
      setGeocodeError(null)
      onChange(null)
      return
    }

    if (typeof newValue === 'string') {
      setInputValue(newValue)
      return
    }

    // User selected a prediction from the list
    setInputValue(newValue.fullText)
    setGeocodeError(null)

    try {
      const resolved = await selectPrediction(newValue)
      onChange({
        lat: resolved.coords.lat,
        lng: resolved.coords.lng,
        displayName:
          resolved.formattedAddress ||
          resolved.formattedDescription ||
          newValue.fullText
      })
    } catch {
      setGeocodeError('Could not resolve location. Please try again.')
      onChange(null)
    }
  }

  const handleRetry = () => {
    setGeocodeError(null)
    reset()
    setInputValue('')
    onChange(null)
  }

  // Fallback to plain TextField when API fails to load
  if (apiState === 'error') {
    return (
      <TextField
        value={inputValue}
        onChange={e => {
          setInputValue(e.target.value)
        }}
        label='Dive Location'
        placeholder='Start typing an address...'
        fullWidth
        error={error}
        helperText={
          helperText || 'Google Places unavailable. Please try again later.'
        }
      />
    )
  }

  const showNoResults =
    inputValue.length >= 3 && predictions.length === 0 && !isLoadingPredictions

  // Determine the helper text to show
  const displayHelperText = geocodeError || resolutionError || helperText

  return (
    <>
      <Autocomplete
        freeSolo
        open={shouldShowPopup}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        filterOptions={x => x}
        options={predictions}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.fullText
        }
        loading={isLoadingPredictions || showNoResults}
        loadingText={showNoResults ? 'No results found' : 'Loading...'}
        noOptionsText='No results found'
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        disabled={
          externalDisabled || apiState === 'loading' || isResolvingPlace
        }
        renderInput={params => (
          <TextField
            {...params}
            label='Dive Location'
            placeholder='Start typing an address...'
            fullWidth
            error={error || !!geocodeError || !!resolutionError}
            helperText={displayHelperText}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingPredictions || isResolvingPlace ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
      {value && !geocodeError && (
        <Typography variant='body2' color='success.main' sx={{ mt: 0.5 }}>
          Location resolved: {value.displayName}
        </Typography>
      )}
      {geocodeError && (
        <Typography
          variant='body2'
          color='error'
          sx={{ mt: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}
        >
          Retry
        </Typography>
      )}
    </>
  )
}

export default AddressAutocomplete
